import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { datePickerClearableProps, datePickerFormItemProps, selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Checkbox, DatePicker, Flex, Form, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { AgentCommonFilterFields } from "../../../../agent/components/commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../../../../agent/components/commonfilters/AgentExtendedFilterModalForm";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionsCategory } from "../../../enums";
import { DEFAULT_BAIL_ACCOUNTS_REPORT_QUERY_PARAMS } from "../../api";
import { BailAccountsReportFilterPageRequest } from "../../types";

type ExtendedFilter = { completedEducationDateRange?: string[]; completedAcademyDateRange?: string[] };

type Props = {
  searchParams: BailAccountsReportFilterPageRequest;
};

export const BailAccountsReportFilterView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<BailAccountsReportFilterPageRequest & ExtendedFilter>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      completedEducationDateRange:
        searchParams.completedEducationDateMin && searchParams.completedEducationDateMax
          ? [searchParams.completedEducationDateMin, searchParams.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        searchParams.completedAcademyDateMin && searchParams.completedAcademyDateMax
          ? [searchParams.completedAcademyDateMin, searchParams.completedAcademyDateMax]
          : undefined,
    });
  }, [searchParams, form]);

  const handleFilterSubmit = ({
    completedEducationDateRange,
    completedAcademyDateRange,
    ...filter
  }: BailAccountsReportFilterPageRequest & ExtendedFilter) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_BAIL_ACCOUNTS_REPORT_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.bailAccount.titles.report")}</h2>}
      extra={
        <ActionButton
          color="danger"
          size="middle"
          variant="outlined"
          icon="reload"
          onClick={() => {
            form.resetFields();
            handleFilterSubmit(form.getFieldsValue());
          }}
        >
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="bailAccountsReportForm">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields>
            <Form.Item
              name="balanceAsOfDate"
              label={t("commissions.bailAccount.filter.balanceAsOfDate")}
              rules={[validations.notFuture]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerClearableProps} maxDate={dayjs()} />
            </Form.Item>
          </AgentCommonFilterFields>

          {isTopAgentPlus ? (
            <>
              <Form.Item
                name="categories"
                label={t("commissions.enums.commissionsCategory_PLUS._label")}
                rules={[validations.none]}
              >
                <Select
                  {...selectStandardProps}
                  mode="multiple"
                  options={Object.values(CommissionsCategory)
                    .filter((category) => category !== CommissionsCategory.PRIMARY)
                    .map((category) => ({
                      value: category,
                      label: t(`commissions.enums.commissionsCategory_PLUS.${category}`),
                    }))}
                />
              </Form.Item>

              <Form.Item
                name="includeUnprocessed"
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("commissions.bailAccount.filter.includeUnprocessed")}</Checkbox>
              </Form.Item>
            </>
          ) : undefined}
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFilterSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
